import type { SceneObject } from '../../types';
import { ContextMenu } from '@radix-ui/themes';
import {
  SceneObjectQuickMenu,
  type SceneObjectQuickMenuActions,
} from './SceneObjectQuickMenu';
import { useEditorStore, useSelectionStore } from '../../stores';
import { assetUrlForDisplaySize } from '../../util/assetResize';
import { parsePivot, pivotCssPercent } from '../../util/pivot';
import { buildBoxBorderStyles } from '../../util/strokeSides';
import {
  buildFillBackground,
  buildShadowFilterStyles,
  buildSvgFill,
  buildTextFillStyles,
  gradientAngleToSvgCoords,
  mergeFillGradient,
  resolveFillMode,
} from '../../util/objectVisualStyle';

export interface SceneObjectProps {
  obj: SceneObject;
  isSelected: boolean;
  isHovered: boolean;
  isLocked: boolean;
  isVisible: boolean;
  isEditing: boolean;
  showQuickMenu?: boolean;
  quickMenuActions?: SceneObjectQuickMenuActions | null;
  onMouseDown: (e: React.MouseEvent, obj: SceneObject) => void;
  onPointerDown: (e: React.PointerEvent, obj: SceneObject) => void;
  onDoubleClick: (e: React.MouseEvent, obj: SceneObject) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onTextChange: (objectId: string, value: string) => void;
  onTextBlur: () => void;
  renderResizeHandles: (obj: SceneObject) => React.ReactNode;
  renderContextMenu: (obj: SceneObject) => React.ReactNode;
  children?: React.ReactNode;
}

function SvgLinearGradientDef({
  id,
  obj,
}: {
  id: string;
  obj: SceneObject;
}) {
  if (resolveFillMode(obj) !== 'gradient') return null;
  const g = mergeFillGradient(obj.fill, obj.fillGradient);
  const coords = gradientAngleToSvgCoords(g.angle);
  return (
    <defs>
      <linearGradient id={id} x1={coords.x1} y1={coords.y1} x2={coords.x2} y2={coords.y2}>
        <stop offset="0%" stopColor={g.colorStart} />
        <stop offset="100%" stopColor={g.colorEnd} />
      </linearGradient>
    </defs>
  );
}

export function SceneObjectComponent({
  obj,
  isSelected,
  isHovered,
  isLocked,
  isVisible,
  isEditing,
  showQuickMenu = false,
  quickMenuActions = null,
  onMouseDown,
  onPointerDown,
  onDoubleClick,
  onContextMenu,
  onMouseEnter,
  onMouseLeave,
  onTextChange,
  onTextBlur,
  renderResizeHandles,
  renderContextMenu,
  children,
}: SceneObjectProps) {
  const zoom = useEditorStore((s) => s.zoom);
  // Guard against stale StageLayer/render props: per-object menu only when exactly one selected.
  const isSingleSelection = useSelectionStore((s) => s.selectedIds.length === 1);
  const isGroupType = obj.type === 'group' || obj.isGroup === true;
  const pivot = parsePivot(obj.pivotPoint);
  const originCss = pivotCssPercent(pivot);
  const gradientId = `fill-grad-${obj.id}`;
  const dropEffects = buildShadowFilterStyles(obj, 'drop');
  const boxEffects = buildShadowFilterStyles(obj, 'box');
  const textFillStyles = buildTextFillStyles(obj);
  const boxFill = buildFillBackground(obj);
  const svgFill = buildSvgFill(obj, gradientId);
  const strokeNone =
    (obj.strokeStyle ?? 'solid') === 'none' || (obj.strokeWidth ?? 2) <= 0;
  const imageSrc =
    obj.type === 'image' && obj.src
      ? assetUrlForDisplaySize(obj.src, obj.width, obj.height, zoom)
      : obj.src;

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger>
        <div
          className={`stage-object${isGroupType ? ' is-group' : ''} ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${isLocked ? 'locked' : ''} ${!isVisible ? 'hidden' : ''}`}
          aria-hidden={!isVisible}
          data-object-id={obj.id}
          style={{
            '--obj-x': `${obj.x}px`,
            '--obj-y': `${obj.y}px`,
            '--obj-width': `${obj.width}px`,
            '--obj-height': `${obj.height}px`,
            '--obj-rotation': `${obj.rotation}deg`,
            '--obj-origin-x': originCss.x,
            '--obj-origin-y': originCss.y,
            '--obj-opacity': obj.opacity,
            '--obj-zindex': obj.zIndex,
          } as React.CSSProperties}
          onPointerDown={(e) => onPointerDown(e, obj)}
          onMouseDown={(e) => onMouseDown(e, obj)}
          onContextMenu={onContextMenu}
          onDoubleClick={(e) => onDoubleClick(e, obj)}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {/* Opacity lives here so chrome (handles / quick menu) stays fully opaque. */}
          <div className="stage-object-visual">
            {obj.type === 'image' && imageSrc && (
              <img
                src={imageSrc}
                alt={obj.name}
                className="stage-object-image"
                draggable={false}
                style={dropEffects}
              />
            )}
            {obj.type === 'text' && (
              isEditing ? (
                <textarea
                  className="stage-object-text-edit"
                  defaultValue={obj.name}
                  autoFocus
                  aria-label="Edit text"
                  style={{
                    fontSize: obj.fontSize ?? 16,
                    textAlign: obj.textAlign ?? 'center',
                    ...textFillStyles,
                    ...dropEffects,
                  }}
                  onBlur={(e) => {
                    onTextChange(obj.id, e.target.value);
                    onTextBlur();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') onTextBlur();
                  }}
                />
              ) : (
                <div
                  className="stage-object-text"
                  style={{
                    fontSize: obj.fontSize ?? 16,
                    textAlign: obj.textAlign ?? 'center',
                    ...textFillStyles,
                    ...dropEffects,
                  }}
                >
                  {obj.name}
                </div>
              )
            )}
            {obj.type === 'shape' && (obj.shapeType === 'path' && obj.pathData ? (
              <svg
                className="stage-object-shape shape-path"
                width="100%"
                height="100%"
                viewBox={`0 0 ${obj.width} ${obj.height}`}
                preserveAspectRatio="none"
                style={dropEffects}
              >
                <SvgLinearGradientDef id={gradientId} obj={obj} />
                <path
                  d={obj.pathData}
                  fill={svgFill}
                  stroke={strokeNone ? 'none' : obj.stroke ?? '#000000'}
                  strokeWidth={obj.strokeWidth ?? 2}
                  strokeDasharray={obj.strokeStyle === 'dashed' ? '8,4' : obj.strokeStyle === 'dotted' ? '2,2' : undefined}
                />
              </svg>
            ) : obj.shapeType === 'triangle' ? (
              <svg
                className="stage-object-shape shape-triangle"
                width="100%"
                height="100%"
                viewBox={`0 0 ${obj.width} ${obj.height}`}
                preserveAspectRatio="none"
                style={dropEffects}
              >
                <SvgLinearGradientDef id={gradientId} obj={obj} />
                <polygon
                  points={`${obj.width / 2},0 0,${obj.height} ${obj.width},${obj.height}`}
                  fill={svgFill}
                  stroke={strokeNone ? 'none' : obj.stroke ?? '#000000'}
                  strokeWidth={obj.strokeWidth ?? 2}
                  strokeDasharray={obj.strokeStyle === 'dashed' ? '8,4' : obj.strokeStyle === 'dotted' ? '2,2' : undefined}
                />
              </svg>
            ) : (
              <div
                className={`stage-object-shape shape-${obj.shapeType ?? 'rectangle'}`}
                style={{
                  ...boxFill,
                  ...buildBoxBorderStyles(obj),
                  ...boxEffects,
                  borderRadius: obj.shapeType === 'circle' ? '50%' : '0',
                }}
              />
            ))}
            {obj.type === 'clip' && (
              <div className="stage-object-clip">
                {obj.name || 'Clip'}
              </div>
            )}
            {obj.type === 'group' && !obj.src && (
              <div className="stage-object-group-frame" aria-hidden="true" />
            )}
            {children}
          </div>
          {renderResizeHandles(obj)}
          {isSelected &&
          !isEditing &&
          isSingleSelection &&
          showQuickMenu &&
          quickMenuActions ? (
            <SceneObjectQuickMenu {...quickMenuActions} />
          ) : null}
        </div>
      </ContextMenu.Trigger>
      <ContextMenu.Content size="1">
        {renderContextMenu(obj)}
      </ContextMenu.Content>
    </ContextMenu.Root>
  );
}
